/**
 * Telling PostHog who someone is.
 *
 * Until this existed, every event we had ever collected was attached to an anonymous browser
 * id. That meant no segmenting by plan, no cross-device continuity, and no way to ask the one
 * question worth asking — what do paying users do that free users don't. Forty-two event types
 * were being captured into a shape that could not answer it.
 *
 * The distinct id is the Firebase uid, deliberately, because it's the only id the backend also
 * knows. Server-side events (see src/services/analytics.service.ts) key on the same value, so a
 * Stripe webhook firing hours later lands on the same person as their clicks.
 */

import posthog from 'posthog-js';

/** Firestore Timestamp | ISO string | Date -> ISO string, or null. */
function toIso(value) {
  if (!value) return null;
  try {
    if (typeof value?.toDate === 'function') return value.toDate().toISOString();
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  } catch {
    return null;
  }
}

/**
 * Attach this browser's events to a person.
 *
 * Safe to call repeatedly — PostHog only sends an $identify when the id or properties actually
 * change, and re-identifying the same user is how the plan stays current after an upgrade.
 *
 * `profile` may be null: auth resolves before the Firestore document is read, and knowing who
 * someone is without yet knowing their plan is still better than anonymous.
 */
export function identifyUser(user, profile) {
  if (!user?.uid) return;

  const subscription = profile?.subscription || {};

  // Properties that describe the person NOW. Overwritten on every identify.
  const set = {
    email: user.email || profile?.email || undefined,
    name: profile?.firstName || user.displayName || undefined,
    plan: subscription.plan || 'free',
    subscription_status: subscription.status || 'none',
    billing_cycle: subscription.billingCycle || undefined,
    // Absent means Stripe everywhere else in the codebase; say so explicitly here so a
    // breakdown by billing source doesn't have a mystery bucket.
    billing_source: subscription.billingSource || 'stripe',
    email_verified: !!user.emailVerified,
  };

  // Properties that describe how they STARTED. Never overwritten, so a cohort stays stable
  // even after someone upgrades.
  const setOnce = {
    signed_up_at: toIso(profile?.createdAt),
    initial_plan: subscription.plan || 'free',
  };

  try {
    posthog.identify(user.uid, set, setOnce);
  } catch (err) {
    // Analytics must never take a page down.
    console.warn('[analytics] identify failed:', err?.message || err);
  }
}

/**
 * Forget who this browser belongs to — call ONLY on an explicit sign-out.
 *
 * Not on every logged-out auth callback: that fires on ordinary anonymous page loads too, and
 * resetting there would mint a new anonymous id on every visit, inflating unique users and
 * severing the pre-signup journey from the signup itself.
 */
export function resetAnalytics() {
  try {
    posthog.reset();
  } catch (err) {
    console.warn('[analytics] reset failed:', err?.message || err);
  }
}
